import { Target, Brain, Dumbbell, Video, Activity, Users2, Salad, BookOpen, Heart } from "lucide-react";
import { ACADEMY } from "../lib/academy-data";
import SectionHeader from "../components/SectionHeader";

const PROGRAM_PILLARS = [
  {
    icon: Target,
    title: "הצד הטכני",
    intro: "פיתוח מיומנויות אישיות לכל שחקן ושחקנית",
    items: [
      "2-3 אימוני יכולת אישית בשבוע, בדגש על עמדת השחקן",
      "אימוני קליעה ייעודיים בבוקר",
      "אימוני אגודה אחר הצהריים בקבוצות הליגה של הפועל מטה יהודה",
      "צילום וניתוח אימונים ומשחקים",
    ],
  },
  {
    icon: Dumbbell,
    title: "הצד הפיזי",
    intro: "תכנית רב-שנתית לפיתוח יכולות גופניות",
    items: [
      "שני אימוני יכולת גופנית בשבוע",
      "אימון אחד בשבוע בחדר כושר",
      "דגש על מהירות, זריזות, כוח מתפרץ וכוח פונקציונאלי",
      "עבודה על גמישות ומניעת פציעות",
    ],
  },
  {
    icon: Brain,
    title: "הצד המנטלי",
    intro: "ליווי מנטלי וחברתי צמוד",
    items: [
      "סדנאות ושיחות מדי שבוע",
      `ליווי מנטלי פרטני ע״י ${ACADEMY.mentalCoach}`,
      "פסיכולוג ספורט ומדריך חברתי",
      "טיפוח כישורים חברתיים וחיזוק האישיות",
    ],
  },
];

const EXTRAS = [
  {
    icon: Video,
    title: "צילום וניתוח",
    text: "ניתוח מעמיק של אימונים ומשחקים לשיפור מתמיד.",
  },
  {
    icon: Activity,
    title: "מאמן אתלטיקה",
    text: "מאמן יכולות גופניות מקצועי, אחראי על אימוני אתלטיקה וחדר כושר.",
  },
  {
    icon: Users2,
    title: "מדריך צמוד",
    text: "מדריך ייעודי שדואג לצרכים הלימודיים, האישיים ותיאום הסעות.",
  },
  {
    icon: Salad,
    title: "תזונה מותאמת",
    text: "ייעוץ תזונתי מקיף — תפריט מאוזן לאורך כל היום.",
  },
  {
    icon: BookOpen,
    title: "תמיכה לימודית",
    text: "שיעורים פרטניים בתיכון החקלאי ומרכז למידה בפנימיה.",
  },
  {
    icon: Heart,
    title: "ציוד חדשני",
    text: "עומד לרשות הספורטאים ציוד חדשני וסביבת אימון מתקדמת.",
  },
];

export default function Program() {
  return (
    <div data-testid="program-page">
      {/* Hero */}
      <section className="relative py-24 md:py-32 overflow-hidden bg-[#0A0A0A]">
        <div className="absolute inset-0 diagonal-stripe opacity-40" />
        <div className="container mx-auto px-6 md:px-10 relative">
          <p className="font-heading text-xs md:text-sm font-bold tracking-[0.3em] text-[#DC2626] uppercase mb-4">
            התכנית
          </p>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-black text-white uppercase tracking-tight leading-[0.9] max-w-4xl">
            תכנית מקצועית
            <br />
            <span className="text-[#DC2626]">בשלושה צירים.</span>
          </h1>
          <p className="font-body text-lg md:text-xl text-neutral-300 mt-6 leading-relaxed max-w-3xl">
            מסגרת הפעילות באקדמיה בנויה כך שכל שחקן ושחקנית יקבלו את כל מה שצריך כדי להתפתח —
            פיזית, מנטלית וטכנית — בליווי מקצועי צמוד של {ACADEMY.leadProfessional}.
          </p>
        </div>
      </section>

      {/* Pillars detail */}
      <section className="py-20 md:py-28 bg-[#050505]">
        <div className="container mx-auto px-6 md:px-10 space-y-8">
          {PROGRAM_PILLARS.map((p, idx) => (
            <div
              key={p.title}
              className="grid grid-cols-1 md:grid-cols-12 gap-8 bg-[#171717] border border-white/10 hover:border-[#DC2626]/50 transition-colors p-8 md:p-12"
              data-testid={`program-pillar-${idx}`}
            >
              <div className="md:col-span-4 flex md:flex-col items-center md:items-start gap-5">
                <div className="w-16 h-16 md:w-20 md:h-20 inline-flex items-center justify-center bg-[#DC2626] text-white rounded-sm flex-shrink-0">
                  <p.icon size={32} strokeWidth={2.5} />
                </div>
                <div>
                  <p className="font-heading text-xs font-bold tracking-[0.3em] text-[#DC2626] uppercase mb-1">
                    {String(idx + 1).padStart(2, "0")} / 03
                  </p>
                  <h3 className="font-heading text-3xl md:text-4xl font-black text-white uppercase">
                    {p.title}
                  </h3>
                </div>
              </div>
              <div className="md:col-span-8">
                <p className="font-body text-lg text-neutral-300 mb-5">{p.intro}</p>
                <ul className="space-y-3">
                  {p.items.map((it) => (
                    <li
                      key={it}
                      className="font-body text-base text-neutral-200 flex items-start gap-3 pb-3 border-b border-white/5 last:border-b-0"
                    >
                      <span className="w-2 h-2 bg-[#DC2626] mt-2 flex-shrink-0" />
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Extras grid */}
      <section className="py-24 md:py-28 bg-[#0A0A0A]">
        <div className="container mx-auto px-6 md:px-10">
          <SectionHeader
            kicker="שירותי מעטפת"
            title="הכל במקום אחד"
            description="מעבר לאימונים — האקדמיה מספקת לכם את כל הכלים כדי להצליח. מתזונה ועד ליווי אישי."
            testId="program-extras-header"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {EXTRAS.map((e, i) => (
              <div
                key={e.title}
                className="group bg-[#171717] border border-white/10 p-7 hover:border-[#DC2626] hover:-translate-y-1 transition-all duration-300"
                data-testid={`extra-card-${i}`}
              >
                <e.icon
                  size={28}
                  strokeWidth={2.5}
                  className="text-[#DC2626] mb-4 group-hover:scale-110 transition-transform"
                />
                <h4 className="font-heading text-xl font-bold text-white mb-2">{e.title}</h4>
                <p className="font-body text-sm text-neutral-400 leading-relaxed">{e.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
